import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CaretLeft } from "@phosphor-icons/react";

/** Resolves the most-likely "back" target for any path so the back button
 * always lands somewhere useful, even on cold loads where browser history
 * is empty (e.g. PWA install, deep link, refresh). */
function resolveParent(pathname) {
  if (pathname === "/" || pathname === "") return null;

  // Detail → list parents
  const detailMap = [
    [/^\/jobs\/[^/]+/, "/jobs"],
    [/^\/job-requests\/[^/]+/, "/job-requests"],
    [/^\/projects\/[^/]+/, "/projects"],
    [/^\/tasks\/[^/]+/, "/tasks"],
    [/^\/work-orders\/[^/]+/, "/work-orders"],
    [/^\/quotes\/[^/]+/, "/quotes"],
    [/^\/invoices\/[^/]+/, "/invoices"],
    [/^\/clients\/[^/]+/, "/clients"],
    [/^\/suppliers\/[^/]+/, "/suppliers"],
    [/^\/vendors\/[^/]+/, "/vendors"],
    [/^\/emergency-jobs\/[^/]+/, "/emergency-jobs"],
    [/^\/properties\/[^/]+/, "/properties"],
    [/^\/forms\/[^/]+/, "/tenant-report-forms"],
    [/^\/tenant-report-forms\/[^/]+/, "/tenant-report-forms"],
  ];
  for (const [re, parent] of detailMap) {
    if (re.test(pathname)) return parent;
  }

  // Default: chop the last segment
  const trimmed = pathname.replace(/\/$/, "");
  const lastSlash = trimmed.lastIndexOf("/");
  if (lastSlash <= 0) return "/";
  return trimmed.substring(0, lastSlash) || "/";
}

/** Smart back button rendered in the mobile header. */
export default function MobileBackButton() {
  const navigate = useNavigate();
  const location = useLocation();

  // Hide on dashboard / true root
  if (location.pathname === "/" || location.pathname === "") return null;

  const handleBack = () => {
    // Use real history if there is any (idx > 0), otherwise fall back to parent
    const hasHistory = window.history.state && (window.history.state.idx ?? 0) > 0;
    if (hasHistory) {
      navigate(-1);
    } else {
      const parent = resolveParent(location.pathname);
      navigate(parent || "/");
    }
  };

  return (
    <button
      onClick={handleBack}
      className="md:hidden -ml-2 p-2 rounded-md hover:bg-muted active:scale-90 transition-transform flex items-center justify-center"
      aria-label="Go back"
      data-testid="mobile-back-btn"
    >
      <CaretLeft size={22} weight="bold" />
    </button>
  );
}

/** Hook: enables iOS-style swipe-from-left-edge to navigate back.
 * Mounts a single global gesture listener; only triggers on touch events
 * that begin in the leftmost 24px of the viewport, drag right > 80px,
 * and travel mostly horizontally (verticalΔ < 60). Skips when the user
 * is dragging an interactive element (form/input/scrollable).
 *
 * Dashboard fallback: when there's no real "back" target (root page +
 * no browser history), the same gesture instead opens the mobile burger
 * drawer via a `necos:open-mobile-nav` CustomEvent that `<MobileNav>`
 * listens for. This gives root pages a native-feeling drawer pull. */
export function useSwipeBack() {
  const navigate = useNavigate();
  const location = useLocation();
  const startRef = useRef(null);

  useEffect(() => {
    // Only on small screens
    const mq = window.matchMedia("(min-width: 768px)");
    if (mq.matches) return;

    const onTouchStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      // Edge zone only
      if (t.clientX > 24) { startRef.current = null; return; }
      // Skip if inside a horizontally-scrollable element
      let el = e.target;
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        if (style.overflowX === "auto" || style.overflowX === "scroll") {
          if (el.scrollWidth > el.clientWidth) { startRef.current = null; return; }
        }
        el = el.parentElement;
      }
      startRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
    };

    const onTouchEnd = (e) => {
      const start = startRef.current;
      startRef.current = null;
      if (!start) return;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = Math.abs(t.clientY - start.y);
      const dt = Date.now() - start.time;
      // Threshold: started within edge zone, traveled >80px right, < 60px vertically, < 600ms
      if (dx > 80 && dy < 60 && dt < 600) {
        const isRoot = location.pathname === "/" || location.pathname === "";
        const hasHistory = window.history.state && (window.history.state.idx ?? 0) > 0;
        if (isRoot || !hasHistory) {
          // No "back" target → open the burger drawer instead.
          window.dispatchEvent(new CustomEvent("necos:open-mobile-nav"));
          return;
        }
        navigate(-1);
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [navigate, location.pathname]);
}
